/**
 * Minimum machine (CLI) version per job kind (ENG-319).
 *
 * The server uses this to skip handing a job to a machine whose CLI is too old
 * to run it. Pure TypeScript, with no Node-only APIs, so the web can import it
 * too (for example, to explain why a job is waiting).
 *
 * Override with the `MIN_MACHINE_VERSION` env var, in one of two forms:
 *  - a plain version, e.g. `1.21.0`, which applies to every job kind
 *  - a JSON map, e.g. `{"default":"1.20.0","start_execution":"1.21.0"}`
 * An invalid value is ignored, and the built-in defaults are used. This never throws.
 */
import { JOB_TYPES } from "./types/mailbox.js";
/** Key in the minimum map that applies to any job kind without its own entry. */
export const MIN_MACHINE_VERSION_DEFAULT_KEY = "default";
/** Command an outdated machine should run to update its CLI. */
export const MACHINE_UPDATE_COMMAND = "compbox update";
/**
 * Built-in minimums. "0.0.0" means every machine qualifies. Raise an entry when
 * a job kind starts needing newer CLI behaviour.
 */
export const MIN_MACHINE_VERSION = Object.freeze({
    default: "0.0.0",
    epic_request: "0.0.0",
    interview_reply: "0.0.0",
    start_execution: "0.0.0",
    connect_repo: "0.0.0",
});
function currentEnv(env) {
    if (env)
        return env;
    return typeof process !== "undefined" ? process.env : {};
}
const SEMVER_RE = /^v?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+[0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*)?$/;
/** Parses a semver string (a leading "v" is allowed). Returns null if it isn't valid. */
export function parseMachineVersion(value) {
    if (typeof value !== "string")
        return null;
    const m = SEMVER_RE.exec(value.trim());
    if (!m)
        return null;
    return {
        major: Number(m[1]),
        minor: Number(m[2]),
        patch: Number(m[3]),
        prerelease: m[4] ? m[4].split(".") : [],
    };
}
function compareIdentifiers(a, b) {
    const aNum = /^\d+$/.test(a);
    const bNum = /^\d+$/.test(b);
    if (aNum && bNum)
        return Math.sign(Number(a) - Number(b));
    if (aNum)
        return -1; // numeric identifiers sort before alphanumeric ones
    if (bNum)
        return 1;
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * Compares two parsed versions using semver precedence. Returns -1, 0 or 1.
 * A prerelease sorts before its release (1.2.0-beta.1 < 1.2.0).
 */
function compareParsed(a, b) {
    for (const key of ["major", "minor", "patch"]) {
        if (a[key] !== b[key])
            return a[key] < b[key] ? -1 : 1;
    }
    if (a.prerelease.length === 0 && b.prerelease.length === 0)
        return 0;
    if (a.prerelease.length === 0)
        return 1;
    if (b.prerelease.length === 0)
        return -1;
    const len = Math.max(a.prerelease.length, b.prerelease.length);
    for (let i = 0; i < len; i++) {
        const x = a.prerelease[i];
        const y = b.prerelease[i];
        if (x === undefined)
            return -1;
        if (y === undefined)
            return 1;
        const c = compareIdentifiers(x, y);
        if (c !== 0)
            return c;
    }
    return 0;
}
/** Compares two version strings. Returns null if either one isn't valid semver. */
export function compareMachineVersions(a, b) {
    const pa = parseMachineVersion(a);
    const pb = parseMachineVersion(b);
    if (!pa || !pb)
        return null;
    return compareParsed(pa, pb);
}
function isValidVersion(value) {
    return typeof value === "string" && parseMachineVersion(value) !== null;
}
/**
 * Parses the `MIN_MACHINE_VERSION` override. Returns the entries to lay over the
 * defaults, or null when it's unset or invalid. Invalid entries in a JSON map
 * are dropped one by one, so a single bad value doesn't discard the rest.
 */
export function parseMinMachineVersionOverride(raw) {
    if (typeof raw !== "string")
        return null;
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    if (trimmed.startsWith("{")) {
        let parsed;
        try {
            parsed = JSON.parse(trimmed);
        }
        catch {
            return null;
        }
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
            return null;
        const out = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (isValidVersion(value))
                out[key] = value.trim();
        }
        return Object.keys(out).length > 0 ? out : null;
    }
    return isValidVersion(trimmed) ? { [MIN_MACHINE_VERSION_DEFAULT_KEY]: trimmed } : null;
}
/**
 * Resolves the minimum CLI version a machine needs for this job kind.
 *
 * Order: the override's entry for this kind, then the override's `default` key,
 * then the built-in entry for this kind, then the built-in `default`. A plain
 * version override applies to every kind.
 */
export function getMinimumFor(jobKind, env) {
    const override = parseMinMachineVersionOverride(currentEnv(env).MIN_MACHINE_VERSION);
    const builtIn = MIN_MACHINE_VERSION;
    return (override?.[jobKind] ??
        override?.[MIN_MACHINE_VERSION_DEFAULT_KEY] ??
        builtIn[jobKind] ??
        MIN_MACHINE_VERSION.default);
}
/**
 * Whether a machine on `version` can take a job of `jobKind`.
 *
 * If the version is unknown (null, empty or not semver), this returns true.
 * Machines that haven't reported a version yet stay eligible so the rollout
 * doesn't strand them.
 */
export function meetsMinimum(version, jobKind, env) {
    const current = parseMachineVersion(version);
    if (!current)
        return true;
    const minimum = parseMachineVersion(getMinimumFor(jobKind, env));
    if (!minimum)
        return true;
    return compareParsed(current, minimum) >= 0;
}
/** Every job kind that has a built-in minimum. */
export const MIN_MACHINE_VERSION_JOB_KINDS = JOB_TYPES;
//# sourceMappingURL=machineVersion.js.map